// =============================================
// Social platforms — templates dla UI prefill + schema.org sameAs generation.
// Autor ≠ Organizacja (inne platformy sensowne per scope).
// =============================================

export interface SocialPlatform {
  id: string;
  displayName: string;
  urlTemplate: string; // z {slug} placeholder
  iconName: string; // lucide-react icon name
  exampleUrl: string;
  notes?: string;
  // Domain aliases — np. Twitter -> ["x.com"] żeby UI matchował także alternative domain.
  // Auto-gen URL nadal używa urlTemplate, ale matching w sameAs poznaje też aliases.
  aliases?: string[];
}

export const AUTHOR_PLATFORMS: SocialPlatform[] = [
  {
    id: "twitter",
    displayName: "Twitter / X",
    urlTemplate: "https://twitter.com/{slug}",
    iconName: "Twitter",
    exampleUrl: "https://twitter.com/jakub_wielbinski",
    aliases: ["x.com"], // Twitter -> X.com rebrand 2023; obie domeny matche w sameAs
  },
  {
    id: "linkedin",
    displayName: "LinkedIn",
    urlTemplate: "https://linkedin.com/in/{slug}",
    iconName: "Linkedin",
    exampleUrl: "https://linkedin.com/in/jakub-wielbinski",
  },
  {
    id: "linktree",
    displayName: "Linktree",
    urlTemplate: "https://linktr.ee/{slug}",
    iconName: "Link2",
    exampleUrl: "https://linktr.ee/jakub.wielbinski",
  },
  {
    id: "facebook",
    displayName: "Facebook",
    urlTemplate: "https://facebook.com/{slug}",
    iconName: "Facebook",
    exampleUrl: "https://facebook.com/jakub.wielbinski",
  },
  {
    id: "medium",
    displayName: "Medium",
    urlTemplate: "https://medium.com/@{slug}",
    iconName: "BookOpen",
    exampleUrl: "https://medium.com/@jakub.wielbinski",
  },
  {
    id: "instagram",
    displayName: "Instagram",
    urlTemplate: "https://instagram.com/{slug}",
    iconName: "Instagram",
    exampleUrl: "https://instagram.com/jakub.wielbinski",
  },
  {
    id: "goldenline",
    displayName: "Goldenline",
    urlTemplate: "https://www.goldenline.pl/{slug}",
    iconName: "Users",
    exampleUrl: "https://www.goldenline.pl/jakub-wielbinski",
  },
  {
    id: "blogger",
    displayName: "Blogger",
    urlTemplate: "https://{slug}.blogspot.com",
    iconName: "FileText",
    exampleUrl: "https://jakub-wielbinski.blogspot.com",
  },
];

export const ORGANIZATION_PLATFORMS: SocialPlatform[] = [
  {
    id: "twitter",
    displayName: "Twitter / X (brand)",
    urlTemplate: "https://twitter.com/{slug}",
    iconName: "Twitter",
    exampleUrl: "https://twitter.com/nierobietegozadarmo",
    aliases: ["x.com"], // Twitter -> X.com rebrand 2023
  },
  {
    id: "facebook",
    displayName: "Facebook Page",
    urlTemplate: "https://facebook.com/{slug}",
    iconName: "Facebook",
    exampleUrl: "https://facebook.com/nierobietegozadarmo",
  },
  {
    id: "linkedin_company",
    displayName: "LinkedIn Company",
    urlTemplate: "https://linkedin.com/company/{slug}",
    iconName: "Linkedin",
    exampleUrl: "https://linkedin.com/company/nierobietegozadarmo",
  },
  {
    id: "instagram",
    displayName: "Instagram",
    urlTemplate: "https://instagram.com/{slug}",
    iconName: "Instagram",
    exampleUrl: "https://instagram.com/nierobietegozadarmo",
  },
  {
    id: "youtube",
    displayName: "YouTube",
    urlTemplate: "https://youtube.com/@{slug}",
    iconName: "Youtube",
    exampleUrl: "https://youtube.com/@nierobietegozadarmo",
  },
];

/** Wygeneruj URL template-owany dla slug. */
export function fillTemplate(template: string, slug: string): string {
  return template.replace(/\{slug\}/g, slug);
}

/** Sygnatura domeny z template (bez protokołu i {slug}). */
function domainSignature(template: string): string {
  return template.replace(/^https?:\/\//, "").replace(/\{slug\}/, "").replace(/\/$/, "").toLowerCase();
}

/** Rozpoznaj platformę autora z URL (domain + aliases). Null gdy brak dopasowania. */
export function matchAuthorPlatform(url: string): SocialPlatform | null {
  const u = (url || "").toLowerCase();
  if (!u) return null;
  for (const p of AUTHOR_PLATFORMS) {
    const domain = domainSignature(p.urlTemplate);
    if (domain && u.includes(domain)) return p;
    if (p.aliases?.some((a) => u.includes(a.toLowerCase()))) return p;
  }
  return null;
}

/**
 * Prosty slugify PL-aware.
 * Unicode escape \u0300-\u036f (combining diacritics) — nie raw combining chars,
 * zeby regex przetrwal edytor/platform encoding transformations.
 */
export function slugify(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ł/g, "l") // ł
    .replace(/Ł/g, "l") // Ł (lowercase'd above, but defensive)
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}
